import { getFromCookieMetadataClient, setInCookieMetadata } from "./cookieDataUtils";
import { algoliaGuestToken } from "./algoliaUtils/constants";
import { encodeBase64, constructAlgoliaGuestUserTokenSA } from "./commonUtils";

/**
 * Client-side function to check if a site is transactional
 *
 * @description This function checks if the given locale corresponds to a transactional site
 * that supports e-commerce features. This is the client-side equivalent of isTransactionalServer.
 *
 * @param {string} locale - The locale string (e.g., "sa-en", "ae-ar")
 * @returns {boolean} true if site is transactional, false otherwise
 * @throws {Error} When environment variables are missing or configuration is invalid
 *
 * @example
 * ```tsx
 * const locale = getClientCookie(localeConstants.NEXT_LOCALE);
 * const isTransactional = isTransactionalClient(locale);
 *
 * if (isTransactional) {
 *   // Show shopping features
 * } else {
 *   // Show catalog-only features
 * }
 * ```
 */
export const isTransactionalClient = (locale: string): boolean => {
  // Check if required environment variables exist
  if (process.env.NEXT_PUBLIC_NON_TRANSACTIONAL_COUNTRIES === undefined) {
    const error = new Error(
      "Missing required environment variable: NEXT_PUBLIC_NON_TRANSACTIONAL_COUNTRIES",
    );
    console.error(error.message);
    throw error;
  }

  if (!process.env.NEXT_PUBLIC_LOCATION_MAPPINGS) {
    const error = new Error("Missing required environment variable: NEXT_PUBLIC_LOCATION_MAPPINGS");
    console.error(error.message);
    throw error;
  }

  try {
    // Validate locale input
    if (!locale || typeof locale !== "string") {
      const error = new Error(`Invalid locale provided: ${locale}`);
      console.error(error.message);
      throw error;
    }

    // Parse environment variables with detailed error handling
    let nonTransactionalCountries: string[];
    let locationMappings: Record<string, string>;

    try {
      // Handle empty string case - if empty, treat as no non-transactional countries
      const nonTransactionalStr = process.env.NEXT_PUBLIC_NON_TRANSACTIONAL_COUNTRIES.trim();
      nonTransactionalCountries =
        nonTransactionalStr === ""
          ? []
          : nonTransactionalStr.split(",").map((country) => country.trim());
    } catch (parseError) {
      const error = new Error(
        `Invalid format in NEXT_PUBLIC_NON_TRANSACTIONAL_COUNTRIES: ${parseError instanceof Error ? parseError.message : "Unknown parsing error"}`,
      );
      console.error(error.message);
      throw error;
    }

    try {
      locationMappings = JSON.parse(process.env.NEXT_PUBLIC_LOCATION_MAPPINGS);
    } catch (parseError) {
      const error = new Error(
        `Invalid JSON in NEXT_PUBLIC_LOCATION_MAPPINGS: ${parseError instanceof Error ? parseError.message : "Unknown parsing error"}`,
      );
      console.error(error.message);
      throw error;
    }

    // Validate parsed data structure
    if (!Array.isArray(nonTransactionalCountries)) {
      const error = new Error(
        "NEXT_PUBLIC_NON_TRANSACTIONAL_COUNTRIES must be a valid comma-separated string",
      );
      console.error(error.message);
      throw error;
    }

    if (
      typeof locationMappings !== "object" ||
      locationMappings === null ||
      Array.isArray(locationMappings)
    ) {
      const error = new Error("NEXT_PUBLIC_LOCATION_MAPPINGS must be a valid JSON object");
      console.error(error.message);
      throw error;
    }

    // Check if locale exists in mappings
    const countryCode = locationMappings[locale];

    if (!countryCode) {
      const error = new Error(
        `No country mapping found for locale "${locale}". Available locales: ${Object.keys(locationMappings).join(", ")}`,
      );
      console.error(error.message);
      throw error;
    }

    // Check if country is in non-transactional countries
    // If country is in non-transactional list, return false (non-transactional)
    // If country is not in non-transactional list, return true (transactional)
    const isTransactional = !nonTransactionalCountries.includes(countryCode);

    return isTransactional;
  } catch (error) {
    // Re-throw the error with additional context
    if (error instanceof Error) {
      console.error("❌ Error in isTransactionalClient:", error.message);
      throw error;
    } else {
      const unknownError = new Error(`Unknown error in isTransactionalClient: ${String(error)}`);
      console.error(unknownError.message);
      throw unknownError;
    }
  }
};

/**
 * Client-side function to check if a site is non-transactional
 *
 * @param {string} locale - The locale string
 * @returns {boolean} true if site is non-transactional, false otherwise
 * @throws {Error} When environment variables are missing or configuration is invalid
 */
export const isNonTransactionalClient = (locale: string): boolean => {
  return !isTransactionalClient(locale);
};

//Getting the user token is a client-side activity 90% of the time, 
//because cookie would already be populated. 10% of the time when 
//cookie needs to be set for the first time, it is a server action.
export const getAlgoliaGuestUserTokenClient = async () => {
  if (typeof window === "undefined") {
    return null;
  }
  const tokenFromCookie = getFromCookieMetadataClient(algoliaGuestToken.ALGOLIA_GUEST_TOKEN);
  return tokenFromCookie ? encodeBase64(tokenFromCookie) : await constructAlgoliaGuestUserTokenSA();
};